import React, { useState, useRef } from 'react';
import { 
  RefreshCw, Upload, FileText, Check, Send, User, X, Eye, Download, 
  Loader2, Printer, Save, Plus, Trash2, DollarSign, Building, Package,
  Edit2, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { generateQuotePDF } from './quotePdfGenerator';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const getCurrencySymbol = (currency) => {
  switch (currency) {
    case 'JPY': return '\u00a5';
    case 'EUR': return '\u20ac';
    case 'USD':
    default: return '$';
  }
};

const formatCurrency = (amount, currency) => {
  const symbol = getCurrencySymbol(currency);
  if (currency === 'JPY') {
    return `${symbol}${Math.round(amount).toLocaleString()}`;
  }
  return `${symbol}${Number(amount).toFixed(2)}`;
};

const QuoteRebranderTab = ({ 
  isDark, 
  authFetch, 
  showToast,
  companySettings,
  onTransferToQuoteGenerator
}) => {
  // Upload & extraction state
  const [rebranderFile, setRebranderFile] = useState(null);
  const [rebranderLoading, setRebranderLoading] = useState(false);
  const [isDraggingRebrander, setIsDraggingRebrander] = useState(false);
  const rebranderFileInputRef = useRef(null);

  // Markup & currency controls
  const [rebranderMarkup, setRebranderMarkup] = useState(30);
  const [rebranderCurrency, setRebranderCurrency] = useState('JPY');

  // Editable quote state (populated after extraction)
  const [quoteData, setQuoteData] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [savingQuote, setSavingQuote] = useState(false);
  const [editMode, setEditMode] = useState('form'); // 'form' or 'preview'

  // File handling
  const handleRebranderFileSelect = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Please upload a PDF file', 'error');
      return;
    }
    setRebranderFile(file);
    setQuoteData(null);
    setOriginalData(null);
  };

  const handleRebranderDrop = (e) => {
    e.preventDefault();
    setIsDraggingRebrander(false);
    handleRebranderFileSelect(e.dataTransfer.files[0]);
  };

  // Extract PDF data
  const extractPdfQuote = async () => {
    if (!rebranderFile) {
      showToast('Please upload a PDF first', 'error');
      return;
    }
    
    setRebranderLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', rebranderFile);
      
      const response = await authFetch(`${API_URL}/api/admin/extract-pdf-quote`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted_data;
        setOriginalData(extracted);
        
        // Build editable quote from extracted data with markup applied
        const lineItems = (extracted.line_items || []).map((item, idx) => ({
          id: (Date.now() + idx).toString(),
          item_type: item.item_type || '',
          item_name: item.item_name || '',
          description: item.description || '',
          quantity: parseInt(item.quantity) || 1,
          unit_price: Math.round((parseFloat(item.unit_price) || 0) * (1 + rebranderMarkup / 100) * 100) / 100,
          original_price: parseFloat(item.unit_price) || 0,
          image_description: item.image_description || ''
        }));

        if (lineItems.length === 0) {
          lineItems.push({
            id: Date.now().toString(),
            item_type: '',
            item_name: '',
            description: '',
            quantity: 1,
            unit_price: 0,
            original_price: 0,
            image_description: ''
          });
        }

        setQuoteData({
          customer_name: extracted.buyer_name || '',
          customer_company: extracted.buyer_company || '',
          customer_contact: extracted.buyer_name || '',
          customer_address: extracted.buyer_address || '',
          customer_phone: extracted.buyer_phone || '',
          customer_email: extracted.buyer_email || '',
          customer_whatsapp: '',
          line_items: lineItems,
          images: [],
          shipping_cost: Math.round((parseFloat(extracted.shipping_cost) || 0) * (1 + rebranderMarkup / 100) * 100) / 100,
          tax_rate: parseFloat(extracted.tax_rate) || 0,
          processing_time: extracted.processing_time || '10-15 Working days',
          delivery_time: extracted.delivery_time || '5-7 working days',
          delivery_method: extracted.delivery_method || 'Express',
          payment_terms: extracted.payment_terms || 'DAP - Delivered At Place',
          notes: extracted.notes || '',
          currency: rebranderCurrency,
          installation_location: 'inside',
          mounting_style: '',
          include_warranty: false,
          include_shipping: true,
          invoice_number: extracted.invoice_number || '',
          original_supplier: extracted.seller_company || '',
          original_currency: extracted.currency || 'USD'
        });
        
        showToast('Quote data extracted! You can now edit all fields before generating the PDF.');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to extract data', 'error');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      showToast('Failed to extract quote data', 'error');
    }
    setRebranderLoading(false);
  };

  // Re-apply markup to all line items
  const reapplyMarkup = (markup) => {
    if (!quoteData || !originalData) return;
    setQuoteData(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => ({
        ...item,
        unit_price: Math.round((item.original_price || 0) * (1 + markup / 100) * 100) / 100
      })),
      shipping_cost: Math.round((parseFloat(originalData.shipping_cost) || 0) * (1 + markup / 100) * 100) / 100
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!quoteData) return { subtotal: 0, taxAmount: 0, total: 0 };
    const subtotal = quoteData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const taxAmount = subtotal * (quoteData.tax_rate / 100);
    const shipping = quoteData.include_shipping ? quoteData.shipping_cost : 0;
    return { subtotal, taxAmount, total: subtotal + shipping + taxAmount };
  };

  // Line item management
  const updateLineItem = (itemId, field, value) => {
    setQuoteData(prev => ({
      ...prev,
      line_items: prev.line_items.map(item => 
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));
  };

  const addLineItem = () => {
    setQuoteData(prev => ({
      ...prev,
      line_items: [...prev.line_items, {
        id: Date.now().toString(),
        item_type: '',
        item_name: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        original_price: 0,
        image_description: ''
      }]
    }));
  };

  const removeLineItem = (itemId) => {
    if (quoteData.line_items.length <= 1) return;
    setQuoteData(prev => ({
      ...prev,
      line_items: prev.line_items.filter(item => item.id !== itemId)
    }));
  };

  const updateField = (field, value) => {
    setQuoteData(prev => ({ ...prev, [field]: value }));
  };

  // PDF Download
  const downloadPDF = async () => {
    if (!quoteData) return;
    setGeneratingPDF(true);
    try {
      const pdfData = {
        ...quoteData,
        quote_number: quoteData.invoice_number || 'REBRANDED',
        created_at: new Date().toISOString(),
      };
      await generateQuotePDF(pdfData, companySettings || {});
      showToast('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF error:', err);
      showToast('Failed to generate PDF', 'error');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Save as generated quote
  const saveQuote = async () => {
    if (!quoteData || !quoteData.customer_name) {
      showToast('Please enter a customer name', 'error');
      return;
    }
    setSavingQuote(true);
    try {
      const saveData = {
        ...quoteData,
        line_items: quoteData.line_items.map(({ original_price, image_description, ...item }) => item),
      };
      const response = await authFetch(`${API_URL}/api/admin/generated-quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData)
      });
      if (response.ok) {
        const saved = await response.json();
        showToast(`Quote saved as ${saved.quote_number || 'new quote'}!`);
      } else {
        showToast('Failed to save quote', 'error');
      }
    } catch (err) {
      showToast('Error saving quote', 'error');
    }
    setSavingQuote(false);
  };

  // Transfer to Quote Generator
  const transferToQuoteGenerator = () => {
    if (!quoteData) return;
    const transferData = {
      ...quoteData,
      line_items: quoteData.line_items.map(({ original_price, image_description, ...item }) => item),
    };
    if (onTransferToQuoteGenerator) {
      onTransferToQuoteGenerator(transferData, rebranderMarkup);
    }
    showToast(`Quote transferred with edits to Quote Generator!`);
  };

  // Reset
  const resetRebrander = () => {
    setRebranderFile(null);
    setQuoteData(null);
    setOriginalData(null);
    setShowPreview(false);
    setRebranderMarkup(30);
    setRebranderCurrency('JPY');
  };

  const totals = calculateTotals();
  const inputStyle = {
    backgroundColor: isDark ? '#1a1a1a' : '#fff',
    color: isDark ? '#fff' : '#000',
    border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
  };

  return (
    <div className="space-y-6">
      {/* Preview Modal */}
      {showPreview && quoteData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPreview(false)} />
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-xl p-8" style={{ backgroundColor: '#fff', color: '#000' }}>
            <button onClick={() => setShowPreview(false)} className="absolute top-4 right-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300 z-10" data-testid="close-rebrander-preview">
              <X className="w-5 h-5" />
            </button>
            
            <div className="quote-preview" style={{ fontFamily: 'Arial, sans-serif', backgroundColor: '#ffffff' }}>
              {/* Header */}
              <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-purple-500">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{companySettings?.company_name || 'Company'}</h1>
                  {companySettings?.company_name_local && <p className="text-gray-600">{companySettings.company_name_local}</p>}
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-purple-600">PROFORMA INVOICE</h2>
                  <p className="text-gray-600">Quote #: {quoteData.invoice_number || 'REBRANDED'}</p>
                  <p className="text-gray-600">Date: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              
              {/* Seller / Buyer */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2"><Building className="w-4 h-4" /> SELLER</h3>
                  <p className="font-semibold">{companySettings?.company_name || ''}</p>
                  <p>{companySettings?.contact_person || ''}</p>
                  <p>{companySettings?.address || ''}</p>
                  <p>Tel: {companySettings?.phone || ''}</p>
                  <p>Email: {companySettings?.email || ''}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2 flex items-center gap-2"><User className="w-4 h-4" /> BUYER</h3>
                  <p className="font-semibold">{quoteData.customer_company || quoteData.customer_name}</p>
                  <p>{quoteData.customer_contact}</p>
                  <p>{quoteData.customer_address}</p>
                  <p>Tel: {quoteData.customer_phone}</p>
                  <p>Email: {quoteData.customer_email}</p>
                </div>
              </div>
              
              {/* Line Items */}
              <table className="w-full mb-8 border-collapse">
                <thead>
                  <tr className="bg-purple-600 text-white">
                    <th className="p-3 text-left">Type</th>
                    <th className="p-3 text-left">Item</th>
                    <th className="p-3 text-left">Description</th>
                    <th className="p-3 text-center">Qty</th>
                    <th className="p-3 text-right">Unit Price</th>
                    <th className="p-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {quoteData.line_items.map((item, idx) => (
                    <tr key={item.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="p-3 border-b">{item.item_type}</td>
                      <td className="p-3 border-b font-medium">{item.item_name}</td>
                      <td className="p-3 border-b text-sm text-gray-600 whitespace-pre-wrap">{item.description}</td>
                      <td className="p-3 border-b text-center">{item.quantity}</td>
                      <td className="p-3 border-b text-right">{formatCurrency(item.unit_price, quoteData.currency)}</td>
                      <td className="p-3 border-b text-right font-medium">{formatCurrency(item.quantity * item.unit_price, quoteData.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Totals */}
              <div className="flex justify-end mb-8">
                <div className="w-64">
                  <div className="flex justify-between py-2 border-b"><span>Subtotal:</span><span>{formatCurrency(totals.subtotal, quoteData.currency)}</span></div>
                  <div className="flex justify-between py-2 border-b"><span>Shipping:</span><span>{quoteData.include_shipping ? formatCurrency(quoteData.shipping_cost, quoteData.currency) : 'Not Included'}</span></div>
                  {quoteData.tax_rate > 0 && <div className="flex justify-between py-2 border-b"><span>Tax ({quoteData.tax_rate}%):</span><span>{formatCurrency(totals.taxAmount, quoteData.currency)}</span></div>}
                  <div className="flex justify-between py-3 text-lg font-bold text-purple-600"><span>TOTAL:</span><span>{formatCurrency(totals.total, quoteData.currency)} {quoteData.currency}</span></div>
                </div>
              </div>
              
              {/* Quote Options */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 p-4 bg-purple-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Installation</p>
                  <p className="font-semibold text-purple-700">{quoteData.installation_location === 'inside' ? 'Indoor' : 'Outdoor'}</p>
                </div>
                {quoteData.mounting_style && <div className="text-center"><p className="text-xs text-gray-500 uppercase">Mounting</p><p className="font-semibold text-purple-700">{quoteData.mounting_style}</p></div>}
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Warranty</p>
                  <p className="font-semibold text-purple-700">{quoteData.include_warranty ? '1 Year Included' : 'Not Included'}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 uppercase">Shipping</p>
                  <p className="font-semibold text-purple-700">{quoteData.include_shipping ? 'Included' : 'Not Included'}</p>
                </div>
              </div>
              
              {/* Delivery & Terms */}
              <div className="grid grid-cols-2 gap-8 mb-8">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">DELIVERY INFORMATION</h3>
                  <p><strong>Processing:</strong> {quoteData.processing_time}</p>
                  <p><strong>Delivery:</strong> {quoteData.delivery_time}</p>
                  <p><strong>Method:</strong> {quoteData.delivery_method}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">PAYMENT TERMS</h3>
                  <p>{quoteData.payment_terms}</p>
                  {companySettings?.paypal_email && <p className="mt-2"><strong>PayPal:</strong> {companySettings.paypal_email}</p>}
                  {companySettings?.bank_details && <p className="mt-2 text-sm whitespace-pre-wrap">{companySettings.bank_details}</p>}
                </div>
              </div>
              
              {quoteData.notes && (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h3 className="font-bold text-gray-700 mb-2">NOTES</h3>
                  <p className="whitespace-pre-wrap">{quoteData.notes}</p>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-4 mt-6 pt-4 border-t">
              <Button onClick={downloadPDF} disabled={generatingPDF} className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white" data-testid="rebrander-download-pdf">
                {generatingPDF ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Download className="w-4 h-4" /> Download PDF</>}
              </Button>
              <Button onClick={() => window.print()} className="flex-1 flex items-center justify-center gap-2 bg-gray-600 hover:bg-gray-700 text-white">
                <Printer className="w-4 h-4" /> Print
              </Button>
              <Button onClick={() => setShowPreview(false)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div className="p-6 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
        <div className="flex items-center gap-3 mb-4">
          <RefreshCw className="w-6 h-6" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
          <div>
            <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Quote Rebrander</h2>
            <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Upload a supplier PDF → AI extracts data → Edit & generate your branded quote</p>
          </div>
        </div>

        {!quoteData && (
          <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: isDark ? 'rgba(0, 255, 255, 0.1)' : 'rgba(14, 165, 233, 0.1)', border: `1px solid ${isDark ? '#00ffff' : '#0ea5e9'}` }}>
            <p className="text-sm" style={{ color: isDark ? '#00ffff' : '#0369a1' }}>
              <strong>How it works:</strong> Upload supplier PDF → AI extracts all data → Edit prices & details → Preview & Download PDF
            </p>
          </div>
        )}

        {/* PDF Upload Area */}
        {!quoteData && (
          <>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${isDraggingRebrander ? 'scale-102' : ''}`}
              style={{
                borderColor: isDraggingRebrander ? (isDark ? '#ff00ff' : '#d946ef') : rebranderFile ? (isDark ? '#00ff88' : '#10b981') : (isDark ? '#444' : '#cbd5e1'),
                backgroundColor: isDraggingRebrander ? (isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)') : rebranderFile ? (isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)') : 'transparent'
              }}
              onDrop={handleRebranderDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingRebrander(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDraggingRebrander(false); }}
              onClick={() => rebranderFileInputRef.current?.click()}
              data-testid="rebrander-drop-zone"
            >
              {rebranderFile ? (
                <div className="flex flex-col items-center">
                  <FileText className="w-12 h-12 mb-3" style={{ color: isDark ? '#00ff88' : '#10b981' }} />
                  <p className="font-semibold" style={{ color: isDark ? '#00ff88' : '#10b981' }}>{rebranderFile.name}</p>
                  <p className="text-sm mt-1" style={{ color: isDark ? '#888' : '#64748b' }}>{(rebranderFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  <button onClick={(e) => { e.stopPropagation(); setRebranderFile(null); }} className="mt-3 text-sm underline" style={{ color: isDark ? '#ff6666' : '#dc2626' }}>Remove file</button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto mb-3" style={{ color: isDark ? '#666' : '#94a3b8' }} />
                  <p className="font-medium" style={{ color: isDark ? '#fff' : '#0f172a' }}>{isDraggingRebrander ? 'Drop PDF here!' : 'Drag & drop a supplier PDF quote'}</p>
                  <p className="text-sm mt-1" style={{ color: isDark ? '#888' : '#64748b' }}>or click to browse</p>
                </>
              )}
              <input ref={rebranderFileInputRef} type="file" className="hidden" accept=".pdf" onChange={(e) => handleRebranderFileSelect(e.target.files[0])} />
            </div>

            {/* Markup & Currency Settings */}
            {rebranderFile && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>Initial Markup %</label>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="200" value={rebranderMarkup} onChange={(e) => setRebranderMarkup(parseInt(e.target.value))} className="flex-1" style={{ accentColor: isDark ? '#ff00ff' : '#d946ef' }} data-testid="markup-slider" />
                    <input type="number" min="0" max="500" value={rebranderMarkup} onChange={(e) => setRebranderMarkup(parseInt(e.target.value) || 0)} className="w-20 p-2 rounded text-center" style={inputStyle} data-testid="markup-input" />
                    <span style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>Output Currency</label>
                  <select value={rebranderCurrency} onChange={(e) => setRebranderCurrency(e.target.value)} className="w-full p-2 rounded" style={inputStyle} data-testid="currency-select">
                    <option value="JPY">JPY (\u00a5)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (\u20ac)</option>
                  </select>
                </div>
              </div>
            )}

            {/* Extract Button */}
            {rebranderFile && (
              <Button onClick={extractPdfQuote} disabled={rebranderLoading} className="w-full mt-4 flex items-center justify-center gap-2 py-3" style={{ background: isDark ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : 'linear-gradient(135deg, #d946ef 0%, #0ea5e9 100%)', color: '#fff' }} data-testid="extract-btn">
                {rebranderLoading ? (<><div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" /> Extracting with AI...</>) : (<><RefreshCw className="w-5 h-5" /> Extract Quote Data</>)}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Editable Quote Form (shown after extraction) */}
      {quoteData && (
        <div className="p-6 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
          {/* Header with actions */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Edit2 className="w-5 h-5" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
              <div>
                <h3 className="font-semibold text-lg" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>Edit Rebranded Quote</h3>
                <p className="text-xs" style={{ color: isDark ? '#888' : '#64748b' }}>
                  {originalData?.seller_company ? `Original: ${originalData.seller_company}` : 'Edit all fields below'}
                  {originalData?.currency ? ` (${originalData.currency})` : ''}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <select value={quoteData.currency} onChange={(e) => updateField('currency', e.target.value)} className="px-3 py-1.5 rounded text-sm font-medium" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#ffff00' : '#eab308', border: `2px solid ${isDark ? '#ffff00' : '#eab308'}` }} data-testid="edit-currency-select">
                <option value="JPY">{'\u00a5'} JPY</option>
                <option value="USD">$ USD</option>
                <option value="EUR">{'\u20ac'} EUR</option>
              </select>
              <Button onClick={() => setShowPreview(true)} size="sm" variant="outline" className="flex items-center gap-1" data-testid="preview-rebranded-btn"><Eye className="w-4 h-4" /> Preview</Button>
              <Button onClick={downloadPDF} disabled={generatingPDF} size="sm" className="flex items-center gap-1" style={{ backgroundColor: isDark ? '#7c3aed' : '#7c3aed', color: '#fff' }} data-testid="download-rebranded-pdf">
                {generatingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
              </Button>
              <Button onClick={resetRebrander} size="sm" variant="outline"><X className="w-4 h-4" /> Start Over</Button>
            </div>
          </div>

          {/* Markup re-apply bar */}
          {originalData && (
            <div className="p-3 rounded-lg mb-4 flex items-center gap-4 flex-wrap" style={{ backgroundColor: isDark ? 'rgba(255, 0, 255, 0.1)' : 'rgba(217, 70, 239, 0.1)', border: `1px solid ${isDark ? '#ff00ff' : '#d946ef'}` }}>
              <span className="text-sm font-medium" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>Markup:</span>
              <input type="number" min="0" max="500" value={rebranderMarkup} onChange={(e) => setRebranderMarkup(parseInt(e.target.value) || 0)} className="w-20 p-1 rounded text-center text-sm" style={inputStyle} />
              <span className="text-sm" style={{ color: isDark ? '#ff00ff' : '#d946ef' }}>%</span>
              <Button size="sm" variant="outline" onClick={() => reapplyMarkup(rebranderMarkup)} className="text-xs" style={{ borderColor: isDark ? '#ff00ff' : '#d946ef', color: isDark ? '#ff00ff' : '#d946ef' }}>
                <RefreshCw className="w-3 h-3 mr-1" /> Re-apply Markup
              </Button>
              <span className="text-xs ml-auto" style={{ color: isDark ? '#888' : '#94a3b8' }}>
                Tip: Edit individual prices directly in line items below
              </span>
            </div>
          )}

          {/* Customer Info */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#ff00ff' : '#ff1493' }}>
              <User className="w-4 h-4" /> Customer Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <input type="text" placeholder="Customer Name *" value={quoteData.customer_name} onChange={(e) => updateField('customer_name', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} data-testid="rb-customer-name" />
              <input type="text" placeholder="Company" value={quoteData.customer_company} onChange={(e) => updateField('customer_company', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
              <input type="text" placeholder="Contact Person" value={quoteData.customer_contact} onChange={(e) => updateField('customer_contact', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
              <input type="text" placeholder="Address" value={quoteData.customer_address} onChange={(e) => updateField('customer_address', e.target.value)} className="p-2 rounded text-sm md:col-span-2" style={inputStyle} />
              <input type="text" placeholder="Phone" value={quoteData.customer_phone} onChange={(e) => updateField('customer_phone', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
              <input type="email" placeholder="Email" value={quoteData.customer_email} onChange={(e) => updateField('customer_email', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
              <input type="text" placeholder="WhatsApp" value={quoteData.customer_whatsapp} onChange={(e) => updateField('customer_whatsapp', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                <Package className="w-4 h-4" /> Line Items
              </h4>
              <Button onClick={addLineItem} size="sm" variant="outline" className="flex items-center gap-1"><Plus className="w-4 h-4" /> Add Item</Button>
            </div>
            
            <div className="space-y-3">
              {quoteData.line_items.map((item, index) => (
                <div key={item.id} className="p-3 rounded-lg" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <input type="text" placeholder="Type" value={item.item_type} onChange={(e) => updateLineItem(item.id, 'item_type', e.target.value)} className="col-span-2 p-2 rounded text-sm" style={{ ...inputStyle, backgroundColor: isDark ? '#222' : '#f8fafc' }} />
                    <input type="text" placeholder="Item Name *" value={item.item_name} onChange={(e) => updateLineItem(item.id, 'item_name', e.target.value)} className="col-span-3 p-2 rounded text-sm" style={{ ...inputStyle, backgroundColor: isDark ? '#222' : '#f8fafc' }} />
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)} className="col-span-1 p-2 rounded text-sm text-center" style={{ ...inputStyle, backgroundColor: isDark ? '#222' : '#f8fafc' }} />
                    <div className="col-span-2 relative">
                      <input type="number" placeholder="Price" value={item.unit_price} onChange={(e) => updateLineItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)} className="w-full p-2 rounded text-sm" style={{ ...inputStyle, backgroundColor: isDark ? '#222' : '#f8fafc' }} />
                      {item.original_price > 0 && (
                        <span className="absolute -top-4 left-0 text-xs" style={{ color: isDark ? '#888' : '#9ca3af' }}>
                          was: {formatCurrency(item.original_price, originalData?.currency || 'USD')}
                        </span>
                      )}
                    </div>
                    <div className="col-span-3 text-right font-semibold p-2" style={{ color: isDark ? '#00ff88' : '#10b981' }}>
                      {formatCurrency(item.quantity * item.unit_price, quoteData.currency)}
                    </div>
                    <button onClick={() => removeLineItem(item.id)} className="col-span-1 p-2 text-red-500 hover:text-red-400" disabled={quoteData.line_items.length <= 1}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <textarea placeholder="Description / Details" value={item.description} onChange={(e) => updateLineItem(item.id, 'description', e.target.value)} rows={2} className="w-full mt-2 p-2 rounded text-sm" style={{ ...inputStyle, backgroundColor: isDark ? '#222' : '#f8fafc' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Shipping, Tax & Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}>
                <DollarSign className="w-4 h-4" /> Shipping & Tax
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="w-24 text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Shipping:</label>
                  <input type="number" value={quoteData.shipping_cost} onChange={(e) => updateField('shipping_cost', parseFloat(e.target.value) || 0)} className="flex-1 p-2 rounded text-sm" style={inputStyle} />
                </div>
                <div className="flex items-center gap-2">
                  <label className="w-24 text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>Tax Rate %:</label>
                  <input type="number" value={quoteData.tax_rate} onChange={(e) => updateField('tax_rate', parseFloat(e.target.value) || 0)} className="flex-1 p-2 rounded text-sm" style={inputStyle} />
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(0, 255, 136, 0.1)' : 'rgba(16, 185, 129, 0.1)', border: `1px solid ${isDark ? '#00ff88' : '#10b981'}` }}>
              <h4 className="text-sm font-semibold mb-3" style={{ color: isDark ? '#00ff88' : '#10b981' }}>Quote Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Subtotal:</span><span>{formatCurrency(totals.subtotal, quoteData.currency)}</span></div>
                <div className="flex justify-between"><span>Shipping:</span><span>{formatCurrency(quoteData.shipping_cost, quoteData.currency)}</span></div>
                {quoteData.tax_rate > 0 && <div className="flex justify-between"><span>Tax ({quoteData.tax_rate}%):</span><span>{formatCurrency(totals.taxAmount, quoteData.currency)}</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t" style={{ borderColor: isDark ? '#00ff88' : '#10b981', color: isDark ? '#00ff88' : '#10b981' }}>
                  <span>TOTAL:</span><span>{formatCurrency(totals.total, quoteData.currency)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery & Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <input type="text" placeholder="Processing Time" value={quoteData.processing_time} onChange={(e) => updateField('processing_time', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
            <input type="text" placeholder="Delivery Time" value={quoteData.delivery_time} onChange={(e) => updateField('delivery_time', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
            <input type="text" placeholder="Delivery Method" value={quoteData.delivery_method} onChange={(e) => updateField('delivery_method', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
            <input type="text" placeholder="Payment Terms" value={quoteData.payment_terms} onChange={(e) => updateField('payment_terms', e.target.value)} className="p-2 rounded text-sm" style={inputStyle} />
          </div>
          
          <textarea placeholder="Additional Notes" value={quoteData.notes} onChange={(e) => updateField('notes', e.target.value)} rows={2} className="w-full p-2 rounded text-sm mb-4" style={inputStyle} />

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button onClick={downloadPDF} disabled={generatingPDF} className="flex items-center justify-center gap-2 py-3" style={{ background: '#7c3aed', color: '#fff' }} data-testid="download-pdf-bottom">
              {generatingPDF ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</> : <><Download className="w-5 h-5" /> Download PDF</>}
            </Button>
            <Button onClick={saveQuote} disabled={savingQuote || !quoteData.customer_name} className="flex items-center justify-center gap-2 py-3" style={{ background: isDark ? 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)' : 'linear-gradient(135deg, #ff1493 0%, #0ea5e9 100%)', color: '#fff' }} data-testid="save-rebranded-quote">
              <Save className="w-5 h-5" /> {savingQuote ? 'Saving...' : 'Save Quote'}
            </Button>
            <Button onClick={transferToQuoteGenerator} className="flex items-center justify-center gap-2 py-3" style={{ background: isDark ? 'linear-gradient(135deg, #00ff88 0%, #00ffff 100%)' : 'linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)', color: isDark ? '#000' : '#fff' }} data-testid="transfer-btn">
              <Send className="w-5 h-5" /> Transfer to Quote Generator
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteRebranderTab;
